import type { ReactNode } from 'react'
import { Component, Suspense } from 'react'
import { QueryErrorResetBoundary } from '@tanstack/react-query'

type QuerySuspenseBoundaryProps = Readonly<{
  children: ReactNode
  errorFallback: (props: { error: Error; reset: () => void }) => ReactNode
  loadingFallback: ReactNode
  onError?: (error: Error) => void
}>

type ErrorBoundaryProps = Readonly<{
  children: ReactNode
  fallback: (props: { error: Error; reset: () => void }) => ReactNode
  onError?: (error: Error) => void
  onReset: () => void
}>

type ErrorBoundaryState = Readonly<{
  error: Error | null
}>

class QuerySuspenseErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    error: null,
  }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error) {
    this.props.onError?.(error)
  }

  reset = () => {
    this.props.onReset()
    this.setState({ error: null })
  }

  render() {
    if (this.state.error != null) {
      return this.props.fallback({
        error: this.state.error,
        reset: this.reset,
      })
    }

    return this.props.children
  }
}

export function QuerySuspenseBoundary({
  children,
  errorFallback,
  loadingFallback,
  onError,
}: QuerySuspenseBoundaryProps) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <QuerySuspenseErrorBoundary
          fallback={errorFallback}
          onError={onError}
          onReset={reset}
        >
          <Suspense fallback={loadingFallback}>{children}</Suspense>
        </QuerySuspenseErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}
